import React, { useCallback, useEffect, useState } from 'react';
import {
  BanknotesIcon, ChatBubbleLeftEllipsisIcon, DocumentTextIcon, EnvelopeIcon, PrinterIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import collectionsService from '../../services/collectionsService';
import { errText } from '../../services/scheduleService';
import { rands } from '../../components/billing/TariffLines';

const ACTIONS = [['phoned', 'Phoned'], ['emailed', 'Emailed'], ['sms', 'SMS / WhatsApp'], ['promise', 'Promise to pay'], ['note', 'Note'], ['handed_over', 'Handed over for collection']];
const ACTION_WORD = { ...Object.fromEntries(ACTIONS), statement: 'Statement', payment: 'Payment' };
const METHODS = [['eft', 'EFT'], ['card', 'Card'], ['cash', 'Cash'], ['cheque', 'Cheque'], ['scheme', 'Medical aid'], ['other', 'Other']];
const BUCKETS = [['current', 'Current'], ['d30', '30'], ['d60', '60'], ['d90', '90'], ['d120', '120+']];
const fmtDay = (d) => (d ? new Date(String(d).length === 10 ? `${d}T12:00:00` : d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const today = () => new Date().toISOString().slice(0, 10);
const inFuture = (days) => new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);
const input = 'rounded-md border border-slate-300 px-2 py-1 text-sm focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30';

// Record a payment, write off, or move a balance to the patient — for one invoice.
const InvoiceAction = ({ inv, mode, onDone, onCancel }) => {
  const [f, setF] = useState({
    payer: inv.responsible === 'scheme' ? 'scheme' : 'patient', amount: inv.balance.toFixed(2), method: inv.responsible === 'scheme' ? 'scheme' : 'eft',
    reference: '', received_on: today(), note: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const go = async () => {
    setBusy(true);
    setErr('');
    try {
      if (mode === 'pay') await collectionsService.payment(inv.draft_id, { ...f, amount: Number(f.amount) });
      else if (mode === 'writeoff') await collectionsService.writeOff(inv.draft_id, Number(f.amount), f.note);
      else await collectionsService.transfer(inv.draft_id, f.note);
      onDone();
    } catch (e) {
      setErr(errText(e));
      setBusy(false);
    }
  };
  return (
    <div className="mt-2 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
      {mode === 'pay' && (
        <div className="flex flex-wrap items-end gap-2 text-xs font-semibold text-slate-600">
          <label>From<select value={f.payer} onChange={(e) => setF((x) => ({ ...x, payer: e.target.value, method: e.target.value === 'scheme' ? 'scheme' : 'eft' }))} className={`mt-0.5 block ${input}`}><option value="patient">Patient</option><option value="scheme">Medical aid</option></select></label>
          <label>Amount (R)<input type="number" min="0" step="0.01" value={f.amount} onChange={set('amount')} className={`mt-0.5 block w-28 ${input}`} /></label>
          <label>How<select value={f.method} onChange={set('method')} className={`mt-0.5 block ${input}`}>{METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
          <label>Received<input type="date" value={f.received_on} max={today()} onChange={set('received_on')} className={`mt-0.5 block ${input}`} /></label>
          <label>Reference<input value={f.reference} onChange={set('reference')} className={`mt-0.5 block w-32 ${input}`} /></label>
        </div>
      )}
      {mode === 'writeoff' && (
        <label className="block text-xs font-semibold text-slate-600">Amount to write off (R)<input type="number" min="0" step="0.01" value={f.amount} onChange={set('amount')} className={`mt-0.5 block w-32 ${input}`} /></label>
      )}
      <input value={f.note} onChange={set('note')} placeholder={mode === 'writeoff' ? 'Why is it being written off? (required)' : mode === 'transfer' ? 'Why (e.g. scheme rejected — benefit exhausted)' : 'Note (optional)'} className={`w-full ${input}`} />
      {mode === 'transfer' && <p className="text-xs text-slate-500">The {rands(inv.balance)} balance becomes the patient’s to pay and shows on their statement.</p>}
      {err && <p className="text-xs text-red-600">{err}</p>}
      <div className="flex gap-2">
        <button onClick={go} disabled={busy || (mode === 'writeoff' && !f.note.trim())} className="rounded-md bg-brand-navy px-3 py-1 text-xs font-semibold text-white disabled:opacity-40">
          {busy ? 'Saving…' : mode === 'pay' ? 'Record payment' : mode === 'writeoff' ? 'Write off' : 'Move to patient'}
        </button>
        <button onClick={onCancel} className="text-xs font-semibold text-slate-500">Cancel</button>
      </div>
    </div>
  );
};

// The statement as the patient will see it; email it or print it (either is recorded).
const StatementModal = ({ patientId, email, onClose, onIssued }) => {
  const [html, setHtml] = useState('');
  const [to, setTo] = useState(email || '');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => {
    collectionsService.previewStatement(patientId).then((d) => setHtml(d.html)).catch((e) => setErr(errText(e)));
  }, [patientId]);

  const issue = async (method) => {
    setBusy(method);
    setErr('');
    try {
      const out = await collectionsService.issueStatement(patientId, method, method === 'email' ? to : undefined);
      if (method === 'print') {
        const w = window.open('', '_blank');
        if (w) { w.document.write(out.html); w.document.close(); w.focus(); w.print(); }
        setMsg('Statement recorded as printed.');
      } else {
        setMsg(`Statement emailed to ${out.sent_to}.`);
      }
      onIssued();
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="flex h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-3">
          <h2 className="flex-1 font-bold text-brand-deep">Statement</h2>
          <button onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100"><XMarkIcon className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 bg-slate-100 p-3">
          {html ? <iframe title="Statement preview" srcDoc={html} className="h-full w-full rounded-lg border border-slate-200 bg-white" /> : <p className="p-6 text-center text-slate-400">{err || 'Making the statement…'}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-5 py-3">
          <input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="Patient’s email" className={`w-64 ${input}`} />
          <button onClick={() => issue('email')} disabled={!!busy || !html} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">
            <EnvelopeIcon className="h-4 w-4" /> {busy === 'email' ? 'Sending…' : 'Email it'}
          </button>
          <button onClick={() => issue('print')} disabled={!!busy || !html} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:opacity-50">
            <PrinterIcon className="h-4 w-4" /> Print
          </button>
          {msg && <span className="text-sm text-green-700">{msg}</span>}
          {err && html && <span className="text-sm text-red-600">{err}</span>}
        </div>
      </div>
    </div>
  );
};

// Log what was done to collect, and when to follow up next.
const FollowUpForm = ({ patientId, onSaved }) => {
  const [f, setF] = useState({ action: 'phoned', note: '', promise_amount: '', promise_date: inFuture(7), next_follow_up: inFuture(7) });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    setErr('');
    try {
      onSaved(await collectionsService.followUp(patientId, {
        action: f.action, note: f.note, next_follow_up: f.next_follow_up || null,
        ...(f.action === 'promise' ? { promise_amount: Number(f.promise_amount), promise_date: f.promise_date } : {}),
      }));
      setF((x) => ({ ...x, note: '', promise_amount: '' }));
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 p-3">
      <div className="flex flex-wrap items-end gap-2 text-xs font-semibold text-slate-600">
        <label>What was done<select value={f.action} onChange={set('action')} className={`mt-0.5 block ${input}`}>{ACTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
        {f.action === 'promise' && (
          <>
            <label>Amount (R)<input type="number" min="0" step="0.01" value={f.promise_amount} onChange={set('promise_amount')} className={`mt-0.5 block w-28 ${input}`} /></label>
            <label>By<input type="date" value={f.promise_date} onChange={(e) => setF((x) => ({ ...x, promise_date: e.target.value, next_follow_up: e.target.value }))} className={`mt-0.5 block ${input}`} /></label>
          </>
        )}
        <label>Follow up on<input type="date" value={f.next_follow_up} onChange={set('next_follow_up')} className={`mt-0.5 block ${input}`} /></label>
      </div>
      <textarea rows={2} value={f.note} onChange={set('note')} placeholder="What was said" className={`w-full resize-none ${input}`} />
      {err && <p className="text-xs text-red-600">{err}</p>}
      <button onClick={save} disabled={busy} className="rounded-md bg-brand-navy px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">{busy ? 'Saving…' : 'Save follow-up'}</button>
    </div>
  );
};

/**
 * AccountPanel — one patient's account in a side panel: what they and their medical
 * aid owe (aged), every final invoice with its balance and actions, the payments,
 * statements and the follow-up history.
 */
const AccountPanel = ({ patientId, onClose, onChanged }) => {
  const [acct, setAcct] = useState(null);
  const [err, setErr] = useState('');
  const [action, setAction] = useState(null); // { draft_id, mode }
  const [statement, setStatement] = useState(false);
  const [showClosed, setShowClosed] = useState(false);

  const load = useCallback(async () => {
    try {
      setAcct(await collectionsService.account(patientId));
    } catch (e) {
      setErr(errText(e));
    }
  }, [patientId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape' && !statement) onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose, statement]);

  const changed = () => { setAction(null); load(); onChanged?.(); };
  const p = acct?.patient;
  const open = (acct?.invoices || []).filter((i) => i.balance > 0.005);
  const closed = (acct?.invoices || []).filter((i) => i.balance <= 0.005);

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-slate-900/30" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="flex h-full w-full max-w-3xl flex-col bg-white shadow-xl">
        <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-brand-deep">{p ? [p.title, p.first_names, p.surname].filter(Boolean).join(' ') : 'Account'}</h2>
            {p && (
              <p className="text-xs text-slate-500">
                {[p.cell_phone, p.email, p.payer === 'private' ? 'Private' : p.membership_number ? `${p.scheme_name} ${p.membership_number}/${p.dependant_code}` : null].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
          {open.length > 0 && (
            <button onClick={() => setStatement(true)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <DocumentTextIcon className="h-4 w-4" /> Statement
            </button>
          )}
          <button onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100"><XMarkIcon className="h-6 w-6" /></button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {err && <p className="text-sm text-red-600">{err}</p>}
          {!acct && !err && <p className="text-slate-400">Loading…</p>}

          {acct && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[['Patient owes', acct.ageing.patient], ['With the medical aid', acct.ageing.scheme]].map(([label, a]) => (
                <div key={label} className="rounded-xl border border-slate-200 p-3">
                  <div className="text-xs font-semibold text-slate-500">{label}</div>
                  <div className="text-xl font-semibold text-slate-900">{rands(a.total)}</div>
                  <div className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-slate-500 tabular-nums">
                    {BUCKETS.map(([k, l]) => (a[k] > 0.005 ? <span key={k} className={k === 'd90' || k === 'd120' ? 'font-semibold text-red-700' : ''}>{l}: {rands(a[k])}</span> : null))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {acct && (
            <section>
              <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">Open invoices</h3>
              {open.length === 0 && <p className="text-sm text-slate-400">Nothing owed.</p>}
              <ul className="space-y-2">
                {open.map((i) => (
                  <li key={i.draft_id} className="rounded-lg border border-slate-200 px-3 py-2.5">
                    <div className="flex flex-wrap items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-slate-800">{i.invoice_no} · {fmtDay(i.service_date)} <span className="font-normal text-slate-500">· {i.age} days</span></div>
                        <div className="text-xs text-slate-500">{[i.procedure_text, i.doctor, i.scheme_name].filter(Boolean).join(' · ')}</div>
                        <div className="mt-0.5 text-xs text-slate-500 tabular-nums">
                          Charged {rands(i.total)} · medical aid paid {rands(i.scheme_paid)} · patient paid {rands(i.patient_paid)}{i.written_off > 0 ? ` · written off ${rands(i.written_off)}` : ''}
                          {i.claim_status === 'rejected' ? <span className="font-semibold text-red-700"> · claim rejected</span> : null}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold tabular-nums text-slate-900">{rands(i.balance)}</div>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${i.responsible === 'patient' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
                          {i.responsible === 'patient' ? 'PATIENT' : 'MEDICAL AID'}
                        </span>
                      </div>
                    </div>
                    {action?.draft_id === i.draft_id ? (
                      <InvoiceAction inv={i} mode={action.mode} onDone={changed} onCancel={() => setAction(null)} />
                    ) : (
                      <div className="mt-1.5 flex flex-wrap gap-3 text-xs font-semibold">
                        <button onClick={() => setAction({ draft_id: i.draft_id, mode: 'pay' })} className="inline-flex items-center gap-1 text-brand-teal hover:underline"><BanknotesIcon className="h-3.5 w-3.5" /> Record payment</button>
                        {i.responsible === 'scheme' && <button onClick={() => setAction({ draft_id: i.draft_id, mode: 'transfer' })} className="text-slate-600 hover:underline">Move balance to patient</button>}
                        <button onClick={() => setAction({ draft_id: i.draft_id, mode: 'writeoff' })} className="text-slate-500 hover:underline">Write off…</button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              {closed.length > 0 && (
                <button onClick={() => setShowClosed((s) => !s)} className="mt-2 text-xs font-semibold text-slate-500 hover:underline">
                  {showClosed ? 'Hide' : 'Show'} {closed.length} settled invoice{closed.length === 1 ? '' : 's'}
                </button>
              )}
              {showClosed && (
                <ul className="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-100 text-xs text-slate-500">
                  {closed.map((i) => <li key={i.draft_id} className="flex justify-between px-3 py-1.5"><span>{i.invoice_no} · {fmtDay(i.service_date)}</span><span className="tabular-nums">{rands(i.total)} settled</span></li>)}
                </ul>
              )}
            </section>
          )}

          {acct && (
            <section>
              <h3 className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500"><ChatBubbleLeftEllipsisIcon className="h-4 w-4" /> Follow-ups</h3>
              <FollowUpForm patientId={patientId} onSaved={(a) => { setAcct(a); onChanged?.(); }} />
              <ul className="mt-3 space-y-2">
                {acct.followups.map((f) => (
                  <li key={f.followup_id} className="text-sm">
                    <div className="text-xs text-slate-400">{fmtDay(f.created_at)} · {f.by_name || '—'} · <span className="font-semibold text-slate-600">{ACTION_WORD[f.action] || f.action}</span>
                      {f.next_follow_up ? ` · follow up ${fmtDay(f.next_follow_up)}` : ''}</div>
                    {f.action === 'promise' && <div className="text-slate-700">Promised {rands(f.promise_amount)} by {fmtDay(f.promise_date)}</div>}
                    {f.note && <div className="text-slate-700">{f.note}</div>}
                  </li>
                ))}
                {acct.followups.length === 0 && <li className="text-sm text-slate-400">Nothing logged yet.</li>}
              </ul>
            </section>
          )}

          {acct && acct.payments.length > 0 && (
            <section>
              <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">Payments &amp; write-offs</h3>
              <table className="w-full text-xs">
                <tbody className="divide-y divide-slate-100">
                  {acct.payments.map((m) => (
                    <tr key={m.payment_id}>
                      <td className="py-1.5 text-slate-500">{fmtDay(m.received_on)}</td>
                      <td className="py-1.5 text-slate-700">{m.kind === 'write_off' ? 'Written off' : m.payer === 'scheme' ? 'Medical aid' : 'Patient'}{m.method && m.kind === 'payment' ? ` · ${m.method.toUpperCase()}` : ''}{m.reference ? ` · ${m.reference}` : ''}</td>
                      <td className="py-1.5 text-slate-500">{m.invoice_no}</td>
                      <td className="py-1.5 text-right font-semibold tabular-nums text-slate-800">{rands(m.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {acct && acct.statements.length > 0 && (
            <section>
              <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">Statements</h3>
              <ul className="space-y-1 text-xs text-slate-600">
                {acct.statements.map((s) => (
                  <li key={s.statement_id}>{fmtDay(s.created_at)} · {s.method === 'email' ? `emailed to ${s.sent_to}` : 'printed'} · {rands(s.patient_due)} due · {s.by_name}</li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
      {statement && <StatementModal patientId={patientId} email={p?.email} onClose={() => setStatement(false)} onIssued={() => { load(); onChanged?.(); }} />}
    </div>
  );
};

export default AccountPanel;
